"use client";
import useIsClient from "@/hooks/useIsClient";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import useShouldFire from "./useShouldFire";
import { toSHA256Hex } from "@/utils/functions/toSHA256Hex";

/** The TikTok standard event name for a completed purchase. */
export const TIKTOK_PURCHASE_EVENT = "CompletePayment";

type Ttq = {
  identify: (data: Record<string, string>) => void;
  track: (event: string, data?: any, options?: any) => void;
  page: () => void;
};

const getTtq = (): Ttq | undefined =>
  typeof window !== "undefined" ? (window as any).ttq : undefined;

export interface TiktokIdentity {
  email?: string;
  phone_number?: string;
  external_id?: string;
}

const normalizeEmail = (value: string) => value.trim().toLowerCase();
/** TikTok wants E.164; keep digits and a leading "+" but never invent a country code. */
const normalizePhone = (value: string | number) =>
  String(value).replace(/[^\d+]/g, "");

/**
 * Builds TikTok's advanced-matching payload.
 *
 * TikTok requires these three fields to be SHA-256 hex hashes AND requires them
 * on `ttq.identify()` — identity sent as `ttq.track()` *properties* is treated
 * as unhashed PII and can take the whole event down with it. Prefers hashing the
 * raw value (so it can be normalized the way TikTok expects) and falls back to
 * the `*_hash` values `prepareCustomerData` already computed.
 */
export const buildTiktokIdentity = (
  customerData?: any,
): TiktokIdentity | null => {
  if (!customerData) return null;
  const identity: TiktokIdentity = {};

  const email = customerData.email
    ? toSHA256Hex(normalizeEmail(String(customerData.email)))
    : customerData.email_hash;
  const phone = customerData.phone
    ? toSHA256Hex(normalizePhone(customerData.phone))
    : customerData.phone_hash;

  if (email) identity.email = email;
  if (phone) identity.phone_number = phone;
  if (customerData.external_id)
    identity.external_id = toSHA256Hex(String(customerData.external_id));

  return Object.keys(identity).length > 0 ? identity : null;
};

/**
 * Last identity handed to `ttq.identify()`, shared across every hook instance
 * (PageViewPixel and useEventTrackers each mount their own) so the same payload
 * isn't re-sent from two places.
 */
let lastSentIdentity = "";

const sendIdentify = (identity: TiktokIdentity | null) => {
  if (!identity) return;
  const key = JSON.stringify(identity);
  if (key === lastSentIdentity) return;
  const ttq = getTtq();
  if (!ttq) return;
  lastSentIdentity = key;
  ttq.identify(identity as unknown as Record<string, string>);
};

/**
 * Purchase-only observability. `tiktok-pixel`'s own `track()` no-ops silently
 * when the pixel hasn't loaded (its `debug` flag is off), which is how
 * CompletePayment used to disappear without a single trace. Mirrors the
 * equivalent logging in useFacebookPixel / useXPixel.
 */
const logTiktokPurchase = (details: {
  event: string;
  data: any;
  eventID?: string;
  ttq_ready: boolean;
}) => {
  fetch("/api/log-info", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      message: "TikTok Pixel CompletePayment Event",
      details: {
        event_name: details.event,
        event_id: details.eventID ?? null,
        ttq_ready: details.ttq_ready,
        identified: !!lastSentIdentity,
        ...details.data,
        contents: JSON.stringify(details.data?.contents ?? []),
      },
      host:
        typeof window !== "undefined"
          ? window.location.hostname
          : "next-server",
      url: typeof window !== "undefined" ? window.location.pathname : "",
    }),
    keepalive: true,
  }).catch(() => {});
};

export default function useTiktokPixel(
  pixel_id?: string | number,
  refresh?: boolean,
  advancedMatching?: any,
) {
  const pathname = usePathname();
  const client = useIsClient();
  const shouldFire = useShouldFire();

  const identity = buildTiktokIdentity(advancedMatching);
  // Key the effects on the identity's VALUE, not its object reference:
  // PageViewPixel rebuilds `advancedMatching` on every render, which used to
  // re-run this effect and fire a duplicate ttq.page() each time.
  const identityKey = identity ? JSON.stringify(identity) : "";

  // Pathname the pixel was initialized on. `loadLibrary` already fires one
  // ttq.page() during init, so that first page view must not be repeated.
  const initializedPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pixel_id || !client || !shouldFire || !pathname) return;
    const w = window as any;
    if (w._ttPixelInitialized?.has?.(pixel_id)) return;

    let ReactPixel: any;
    import("tiktok-pixel")
      .then((module) => (ReactPixel = module.default))
      .then(() => {
        if (!w._ttPixelInitialized) w._ttPixelInitialized = new Set();
        // Init exactly once per pixel id (mirrors the Meta guard in
        // useFacebookPixel). tiktok-pixel's init() calls ttq.load/identify/page
        // internally, so a second init would only duplicate the page view.
        if (w._ttPixelInitialized.has(pixel_id)) return;
        // 2nd arg is forwarded straight to ttq.identify() inside loadLibrary —
        // the only advanced-matching path this library version supports.
        ReactPixel.init(pixel_id, identity ?? {});
        w._ttPixelInitialized.add(pixel_id);
        if (identityKey) lastSentIdentity = identityKey;
        initializedPathRef.current = pathname;
      });
    // `identity`/`identityKey` are intentionally not deps — init happens once
    // and later identity changes are handled by the identify effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, pixel_id, refresh, shouldFire, pathname]);

  // SPA navigations: ttq.page() per route change, skipping the one init did.
  useEffect(() => {
    if (!pixel_id || !client || !shouldFire || !pathname) return;
    const ttq = getTtq();
    if (!ttq) return; // not loaded yet — init's own ttq.page() covers this route
    if (initializedPathRef.current === pathname) return;
    initializedPathRef.current = pathname;
    ttq.page();
  }, [client, pixel_id, shouldFire, pathname]);

  // Identity is often only known AFTER init (a guest filling in the checkout
  // form). init() short-circuits once window.ttq exists, so re-send it directly.
  useEffect(() => {
    if (!client || !shouldFire || !identityKey) return;
    sendIdentify(JSON.parse(identityKey) as TiktokIdentity);
  }, [client, shouldFire, identityKey]);

  /** Sends advanced matching immediately, before a conversion event. */
  function identifyUser(customerData?: any) {
    sendIdentify(buildTiktokIdentity(customerData));
  }

  function trackEvent(event: any, data: any, eventID?: string) {
    const isPurchase = event === TIKTOK_PURCHASE_EVENT;
    const ttq = getTtq();
    if (!ttq) {
      if (isPurchase)
        logTiktokPurchase({ event, data, eventID, ttq_ready: false });
      return;
    }
    // Calls ttq directly rather than going through tiktok-pixel's track(): its
    // verifyInit() returns silently when the module flag is unset, and the
    // dynamic import added a chunk-load round trip at exactly the moment the
    // purchase event races page unload.
    ttq.track(event, { ...data }, eventID ? { event_id: eventID } : {});
    if (isPurchase)
      logTiktokPurchase({ event, data, eventID, ttq_ready: true });
  }

  return { trackEvent, identifyUser };
}
